import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'utils/modifiedTranslation';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CheckIcon from '@material-ui/icons/Check';
import InputFieldContainer from 'components/InputFieldContainer';
import useStyles from './styles';

/**
 * A multi drop down select input with autocomplete
 */
export const InputSelect = ({
  id,
  placeholder,
  options,
  values,
  isMultipleSelection,
  rootClassName,
  onChange,
  label,
  helperText,
  labelClassName,
  isRequired,
  popupOpenTrigger,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef();
  const classes = useStyles();
  const collectedValues = useRef([]);
  const defaultValue = isMultipleSelection ? values : null;

  const handleChange = (event, selectedValues) => {
    collectedValues.current = selectedValues;
    if (isMultipleSelection && !Array.isArray(collectedValues.current)) {
      onChange([collectedValues.current]);
    } else {
      onChange(collectedValues.current);
    }
  };

  useEffect(() => {
    if (popupOpenTrigger && inputRef.current) {
      inputRef.current.focus();
    }
  }, [popupOpenTrigger]);

  return (
    <InputFieldContainer
      label={label}
      helperText={helperText}
      labelClassName={labelClassName}
      isRequired={isRequired}
    >
      <Autocomplete
        id={id}
        disableClearable
        popupIcon={null}
        multiple={isMultipleSelection}
        className={cx(
          classes.input,
          rootClassName,
          { [classes.filledInput]: !!values?.length && isMultipleSelection },
        )}
        options={options}
        getOptionLabel={(option) => option?.label && t(option.label)}
        renderOption={(option, { selected }) => (
          <>
            {t(option.label)}
            {selected && <CheckIcon fontSize="small" color="primary" />}
          </>
        )}
        getOptionSelected={(option, valueObject) => option?.value === valueObject?.value}
        defaultValue={defaultValue}
        key={defaultValue}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField
            variant="outlined"
            inputRef={inputRef}
            placeholder={t(placeholder)}
            {...params}
          />
        )}
        ChipProps={{
          className: classes.chipItem,
        }}
        ListboxProps={{
          className: classes.list,
        }}
      />
    </InputFieldContainer>
  );
};

InputSelect.propTypes = {
  /**
  * ID of the input.
  */
  id: PropTypes.string,
  /**
   * Label of the input.
   */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * String for displaying under the input
   */
  helperText: PropTypes.string,
  /**
   * Classname for the label
   */
  labelClassName: PropTypes.string,
  /**
   * Flag to make input required to be filled.
   */
  isRequired: PropTypes.bool,
  /**
   * Placeholder of the input.
   */
  placeholder: PropTypes.string.isRequired,
  /**
   * Array of options for the dropdown list
   */
  options: PropTypes.array.isRequired,
  /**
   * Array of selected options. Array of objects in which presents labels and values of these options.
   */
  values: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  /**
   * A flag for enabling several options selection. Works like checkbox.
   */
  isMultipleSelection: PropTypes.bool,
  /**
   * Classname for the root div
   */
  rootClassName: PropTypes.string,
  /**
   * Function to call when option selected
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Boolean to focus on input
  */
  popupOpenTrigger: PropTypes.bool,
};

InputSelect.defaultProps = {
  id: '',
  isMultipleSelection: true,
  rootClassName: '',
  label: '',
  helperText: '',
  labelClassName: '',
  isRequired: false,
  popupOpenTrigger: false,
};
